import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { OFFER_TEXT, steps } from './lang';
import { PAGE_TEXT } from '../lang';
import {
  useGetOfferDetails,
  useAcceptOffer,
  useRejectOffer,
} from '../../../../graphql';
import {
  OFFER_STATUS,
  OFFER_STATUS_TEXT,
} from '../../../../../Assessment/GSAssessment/components/GSToDoList/constants';
import {
  Alert,
  type AlertDetails,
  Button,
  Dialog,
  Loading,
} from '../../../../../../components';
import { getRequest, type GetRequestData } from '../../../../../../utils';

export type NoticeOfOfferProps = {
  applicationNo: string;
  refetchTodoList: () => void;
};

type DialogProps = {
  openDialog: boolean;
  dialogTitle: string;
  children: React.ReactNode;
  dialogAction: () => void;
  onDialogClose: () => void;
};

const getInformationBox = (label: string, value: string): React.ReactNode => {
  return (
    <div className="flex flex-row w-full pt-1 pb-1 pl-2">
      <div className="w-80">{label + ': '}</div>
      <div>{value}</div>
    </div>
  );
};

export const NoticeOfOffer: React.FunctionComponent<NoticeOfOfferProps> = (
  props: NoticeOfOfferProps,
) => {
  const { applicationId } = useParams();
  const { applicationNo, refetchTodoList } = props;
  const [confirmOfferActionDialogOpen, setConfirmOfferActionDialogOpen] =
    useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogProps | null>(null);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'success',
  });
  const [asycLoading, setAsycLoading] = useState<boolean>(false);

  const { offerDetails, loading, error, refetch } = useGetOfferDetails(
    applicationId as string,
  );
  const {
    OFFER_INFORMATION,
    OFFER_STATUS_LABEL,
    DEPARTMENT,
    PROGRAMME,
    STUDY_MODE,
    PROGRAMME_CODE,
    STUDY_PERIOD,
    CREDIT_REQUIREMENT,
    STUDY_COMMENCEMENT,
    REPORTING_DATE,
    OFFER,
    CONDITION_OFFER,
    FIRM_OFFER,
    CONDITIONS,
    CONDITIONS_DEADLINE,
    STUDENT_VISA_PERMIT,
    STUDENT_VISA_PERMIT_CONTENT,
    REGISTRATION_PROCEDURES,
    REGISTRATION_PROCEDURES_CONTENT_2024_02_03,
    REGISTRATION_PROCEDURES_CONTENT_2025_01_02_03,
  } = OFFER_TEXT;

  const {
    status,
    department,
    programme,
    studyMode,
    programmeCode,
    normalPeriodOfStudy,
    creditRequirement,
    reportingDutyDate,
    commencementDate,
    isFirmOffer,
    conditions = [],
    conditionFulfillingDeadline,
    fundingLetter,
    entryYear,
  } = offerDetails;

  let registrationProcedureContent = '';
  if (['2024-02', '2024-03'].includes(entryYear)) {
    registrationProcedureContent = REGISTRATION_PROCEDURES_CONTENT_2024_02_03;
  } else if (['2025-01', '2025-02', '2025-03'].includes(entryYear)) {
    registrationProcedureContent =
      REGISTRATION_PROCEDURES_CONTENT_2025_01_02_03;
  }

  const offerInformation: Array<{
    label: string;
    value: React.ReactNode;
  }> = [
    {
      label: DEPARTMENT,
      value: department,
    },
    {
      label: PROGRAMME,
      value: programme,
    },
    {
      label: STUDY_MODE,
      value: studyMode,
    },
    {
      label: PROGRAMME_CODE,
      value: programmeCode,
    },
    {
      label: STUDY_PERIOD,
      value: normalPeriodOfStudy,
    },
    {
      label: CREDIT_REQUIREMENT,
      value: creditRequirement,
    },
    {
      label: STUDY_COMMENCEMENT,
      value: commencementDate,
    },
    {
      label: REPORTING_DATE,
      value: reportingDutyDate,
    },
    {
      label: OFFER,
      value: isFirmOffer ? FIRM_OFFER : CONDITION_OFFER,
    },
    ...(!isFirmOffer
      ? [
          {
            label: CONDITIONS,
            value: (
              <div>
                {conditions.map((condition, index) => {
                  return (
                    <div key={index}>
                      <div>
                        {index + 1}. {condition.detail}
                      </div>
                    </div>
                  );
                })}
              </div>
            ),
          },
          {
            label: CONDITIONS_DEADLINE,
            value: conditionFulfillingDeadline,
          },
        ]
      : []),
  ];

  const acceptOffer = useAcceptOffer();
  const rejectOffer = useRejectOffer();

  const handleAcceptOffer = async (): Promise<void> => {
    setAsycLoading(true);
    try {
      await acceptOffer({ variables: { id: applicationId } });
      refetchTodoList();
      setAlertDetails({
        alertContent: 'Offer accepted successfully. ',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Failed to accept offer. ',
        alertType: 'error',
      });
    } finally {
      setAlertOpen(true);
      setAsycLoading(false);
      refetch();
    }
  };

  const handleDeclineOffer = async (): Promise<void> => {
    setAsycLoading(true);
    try {
      await rejectOffer({ variables: { id: applicationId } });
      refetchTodoList();
      setAlertDetails({
        alertContent: 'Offer declined successfully. ',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Failed to decline offer. ',
        alertType: 'error',
      });
    } finally {
      setAlertOpen(true);
      setAsycLoading(false);
      refetch();
    }
  };

  const onAcceptOfferClick = (): void => {
    setConfirmOfferActionDialogOpen(true);
    setDialogProps({
      openDialog: true,
      onDialogClose: () => {
        setConfirmOfferActionDialogOpen(false);
      },
      dialogTitle: 'Accept Offer',
      dialogAction: handleAcceptOffer,
      children: (
        <div className="ml-8 mr-8">
          Are you sure you want to accept the offer?
        </div>
      ),
    });
  };

  const onDeclineOfferClick = (): void => {
    setConfirmOfferActionDialogOpen(true);
    setDialogProps({
      openDialog: true,
      onDialogClose: () => {
        setConfirmOfferActionDialogOpen(false);
      },
      dialogTitle: 'Decline Offer',
      dialogAction: handleDeclineOffer,
      children: (
        <div className="ml-8 mr-8">
          Are you sure you want to decline the offer?
        </div>
      ),
    });
  };

  const handleDownloadOffer = async (): Promise<void> => {
    setAsycLoading(true);
    try {
      const getRequestData: GetRequestData = {
        url: `/files/${fundingLetter?.id}`,
        responseType: 'blob',
        headers: {
          Accept: 'text/pdf',
        },
      };
      const response = await getRequest(getRequestData);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${applicationNo}_Notice_of_Offer.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error downloading offer.' + (error as Error)?.message,
        alertType: 'error',
      });
      setAlertOpen(true);
    } finally {
      setAsycLoading(false);
    }
  };

  const displayOfferButtons = status === OFFER_STATUS.RELEASED;

  if (error) {
    return (
      <Alert
        openAlert={true}
        alertDetails={{
          alertContent: 'Failed to fetch offer details. ',
          alertType: 'error',
        }}
        setOpenAlert={() => {}}
      />
    );
  }

  return loading ? (
    <Loading />
  ) : (
    <Container>
      {asycLoading && <Loading />}
      <Dialog
        dialogTitle={dialogProps?.dialogTitle ?? ''}
        onDialogClose={() => dialogProps?.onDialogClose()}
        openDialog={confirmOfferActionDialogOpen}
        onDialogAction={dialogProps?.dialogAction}
        dialogAction="Confirm"
        cancelAction="Cancel"
      >
        {dialogProps?.children}
      </Dialog>
      <Alert
        openAlert={alertOpen}
        alertDetails={alertDetails}
        setOpenAlert={setAlertOpen}
      />

      <div className="mb-2">
        {fundingLetter?.id ? (
          <Typography
            variant="subtitle2"
            className="print-offer-icon underline text-[#951D1D] cursor-pointer"
            onClick={handleDownloadOffer}
          >
            <PictureAsPdf />
            <b>Print notice of offer</b>
          </Typography>
        ) : (
          <div className="font-bold">Notice of Offer is not Ready</div>
        )}
      </div>

      <div className="w-full flex flex-row align-center">
        {getInformationBox(OFFER_STATUS_LABEL, OFFER_STATUS_TEXT[status])}
      </div>
      <section>
        <h2>{OFFER_INFORMATION}</h2>
        {offerInformation.map(info => {
          return getInformationBox(info.label, info.value as string);
        })}
      </section>
      <section>
        <h2>{steps.title}</h2>
        <div className="p-2" dangerouslySetInnerHTML={{ __html: steps.desc }} />
        <div
          className="px-2"
          dangerouslySetInnerHTML={{ __html: steps.content }}
        />
      </section>
      <section>
        <h2>{STUDENT_VISA_PERMIT}</h2>
        <div
          className="p-2"
          dangerouslySetInnerHTML={{ __html: STUDENT_VISA_PERMIT_CONTENT }}
        />
      </section>
      <section>
        <h2>{REGISTRATION_PROCEDURES}</h2>
        <div
          className="p-2"
          dangerouslySetInnerHTML={{ __html: registrationProcedureContent }}
        />
      </section>
      {displayOfferButtons && (
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '2rem',
            gap: '3rem',
          }}
        >
          <Button
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
            }}
            onClick={onAcceptOfferClick}
          >
            {PAGE_TEXT.ACCEPT_MY_OFFER}
          </Button>
          <Button
            variant="text"
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
            }}
            onClick={onDeclineOfferClick}
          >
            {PAGE_TEXT.DECLINE_MY_OFFER}
          </Button>
        </Grid>
      )}
    </Container>
  );
};

const Container = styled.div`
  h2 {
    font-size: 1.2rem;
    font-weight: 500;
    margin-top: 1rem;
  }

  line-height: 1.625;

  p {
    p {
      margin: 8px;
    }
  }

  .color {
    color: #951d1d;
    font-style: normal;
    text-decoration: underline;
  }

  .marg-text {
    text-decoration: underline;
    margin-bottom: 1.25rem;
  }

  .email-link {
    color: #951d1d;
    text-decoration: underline;
    font-style: normal;
  }

  .webside-link {
    color: #951d1d;
    cursor: pointer;
    text-decoration: underline;
    font-style: normal;
  }

  .pd {
    padding: 0.5rem;
  }
  .italic {
    font-style: italic;
  }
`;
